import React from 'react';
import { useCombinedRefs, KEY_CODES } from 'common';

const SELECTOR = 'data-ccfk-expansion-panel-header';

/** Keyboard navigation logic for ExpansionPanelsContainer */
const handleKeyDown = ({ keyboardNavigation }: ExpansionPanelsContainerProps) => (event) => {
  if (!keyboardNavigation) {
    return;
  }
  const { key } = event;
  const current = document.activeElement;
  const shouldNotFocus = element => !element.hasAttribute(SELECTOR) || element.hasAttribute('disabled');
  const prevItem = item =>
    item &&
    item.parentElement.previousElementSibling &&
    item.parentElement.previousElementSibling.querySelector(`[${SELECTOR}]`);
  const nextItem = item =>
    item &&
    item.parentElement.nextElementSibling &&
    item.parentElement.nextElementSibling.querySelector(`[${SELECTOR}]`);
  const shiftFocus = (currentItem: {}, traverse: (any) => any = () => {}) => {
    let nextFocus = traverse(currentItem);
    while (nextFocus) {
      if (shouldNotFocus(nextFocus)) {
        nextFocus = traverse(nextFocus);
      } else {
        nextFocus.focus();
        return true;
      }
    }
    return false;
  };

  switch (key) {
    case KEY_CODES.ARROW_UP:
      event.preventDefault();
      shiftFocus(current, prevItem);
      break;
    case KEY_CODES.ARROW_DOWN:
      event.preventDefault();
      shiftFocus(current, nextItem);
      break;
    default:
      break;
  }
};

export interface ExpansionPanelsContainerProps extends React.ComponentPropsWithRef<'div'> {
  /** Place multiple ExpansionPanel components here. */
  children: React.ReactNode;
  /** Toggles keyboard navigation between panels. */
  keyboardNavigation?: boolean;
}

/** ExpansionPanelsContainer is a wrapper component that assists with keyboard navigation.
 * Place multiple ExpansionPanel components within this container.
 * All additional props will be forwarded to the root element.
 * */
const ExpansionPanelsContainer = React.forwardRef<HTMLElement, ExpansionPanelsContainerProps>((props: ExpansionPanelsContainerProps, ref) => {
  const containerRef = React.useRef(null);
  const combinedRef = useCombinedRefs(ref, containerRef);
  // eslint-disable-next-line no-unused-vars
  const { keyboardNavigation, ...otherProps } = props;
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      data-ccfk-expansion-panel-container
      ref={combinedRef}
      onKeyDown={handleKeyDown(props)}
      {...otherProps}
    >
      {props.children}
    </div>
  );
});

ExpansionPanelsContainer.defaultProps = {
  keyboardNavigation: true
};
ExpansionPanelsContainer.displayName = 'ExpansionPanelsContainer';

/* Exports */
export { ExpansionPanelsContainer as default };
